import { hasColorInRange, OwlHsvRanges } from './color.js';
import { assertRectInside, rectFromLTRB, relativeTo } from './image.js';
import { detectOwlWindow } from './cropper.js';
import { OWL_ITEM_NAME_PREFIX_ADVANCE, OWL_TOTAL_COUNT_PREFIX_ADVANCE, } from './generated/owl-prefix-metrics.js';
export { OWL_ITEM_NAME_PREFIX, OWL_ITEM_NAME_PREFIX_ADVANCE, OWL_TOTAL_COUNT_PREFIX, OWL_TOTAL_COUNT_PREFIX_ADVANCE, OWL_ESULTS_PROFILE_ANCHOR_RECT, OWL_TOTA_PROFILE_ANCHOR_RECT, } from './generated/owl-prefix-metrics.js';
/** The only supported Owl result-window geometry. */
export const OWL_WINDOW_WIDTH = 434;
export const OWL_WINDOW_HEIGHT = 317;
export const OWL_ROW_COUNT = 8;
export const OWL_ROW_HEIGHT_STEP = 22;
export const OWL_ROW_LOCAL_TOP = 98;
export const OWL_ROW_CONTENT_Y_OFFSET = 1;
export const OWL_ROW_CONTENT_HEIGHT = 21;
/** Fixed glyph-line origin inside every Owl result row cell. */
export const OWL_ROW_TEXT_LINE_Y = 5;
/** Left inset from the visual ID/store column boundary to its text region. */
export const OWL_ID_STORE_TEXT_INSET = 3;
export const OWL_BUNDLE_TEXT_WIDTH = 28;
export const OWL_PRICE_TEXT_WIDTH = 73;
export const OWL_OVERLAP_PRICE_TEXT_WIDTH = 83;
export const OWL_QUANTITY_TEXT_WIDTH = 30;
export const OWL_TABLE_WIDTH = 434;
export const OWL_BASE_BOUNDARIES = [16, 82, 150, 180, 255, 290];
export const OWL_BILLION_DIGIT_WIDTH = 7;
/** Extra pixels included to the left of the normal price column on overlap rows. */
export const OWL_BILLION_PRICE_LEFT_EXTENSION = 10;
/** Shared logical origin of the regular header prefixes inside the Owl window. */
export const OWL_HEADER_TEXT_START_X = 18;
export const OWL_ITEM_NAME_TEXT_START_X = OWL_HEADER_TEXT_START_X + OWL_ITEM_NAME_PREFIX_ADVANCE;
export const OWL_TOTAL_COUNT_TEXT_START_X = OWL_HEADER_TEXT_START_X + OWL_TOTAL_COUNT_PREFIX_ADVANCE;
const ITEM_NAME_LOCAL = rectFromLTRB(OWL_ITEM_NAME_TEXT_START_X, 13, 420, 25);
const TOTAL_COUNT_LOCAL = rectFromLTRB(OWL_TOTAL_COUNT_TEXT_START_X, 27, 131, 39);
const PAGE_INDICATOR_LOCAL = rectFromLTRB(150, 294, 285, 306);
const WINDOW_ANCHOR_LOCAL_Y = 68;
function isOwlWindowRect(rect) {
    return rect.width === OWL_WINDOW_WIDTH && rect.height === OWL_WINDOW_HEIGHT;
}
function knownDetection(rect) {
    return {
        rect,
        matchedSide: 'left',
        score: 1,
        anchor: { x: rect.x, y: rect.y + WINDOW_ANCHOR_LOCAL_Y },
    };
}
function buildCell(key, rowRect, left, right) {
    return {
        key,
        rect: {
            x: rowRect.x + left,
            y: rowRect.y,
            width: right - left,
            height: rowRect.height,
        },
    };
}
function buildRowLayout(source, windowRect, index, detectOverlap) {
    const top = OWL_ROW_LOCAL_TOP + OWL_ROW_CONTENT_Y_OFFSET + index * OWL_ROW_HEIGHT_STEP;
    const rowRect = {
        x: windowRect.x,
        y: windowRect.y + top,
        width: OWL_TABLE_WIDTH,
        height: OWL_ROW_CONTENT_HEIGHT,
    };
    const bundleRight = OWL_BASE_BOUNDARIES[3];
    const billionDigitRect = {
        x: windowRect.x + bundleRight - OWL_BILLION_DIGIT_WIDTH,
        y: rowRect.y,
        width: OWL_BILLION_DIGIT_WIDTH,
        height: rowRect.height,
    };
    const hasBillionPriceOverlap = detectOverlap
        && hasColorInRange(source, billionDigitRect, OwlHsvRanges.redText);
    const cells = {};
    cells.id = buildCell('id', rowRect, OWL_BASE_BOUNDARIES[0] + OWL_ID_STORE_TEXT_INSET, OWL_BASE_BOUNDARIES[1]);
    cells.storeName = buildCell('storeName', rowRect, OWL_BASE_BOUNDARIES[1] + OWL_ID_STORE_TEXT_INSET, OWL_BASE_BOUNDARIES[2]);
    const bundleLeft = OWL_BASE_BOUNDARIES[2];
    cells.bundle = buildCell('bundle', rowRect, bundleLeft, bundleLeft + OWL_BUNDLE_TEXT_WIDTH);
    // The price crop extends left only so it can include the seven-pixel
    // overflow digit plus its three-pixel leading padding.
    const priceLeft = hasBillionPriceOverlap
        ? OWL_BASE_BOUNDARIES[3] - OWL_BILLION_PRICE_LEFT_EXTENSION
        : OWL_BASE_BOUNDARIES[3];
    const priceWidth = hasBillionPriceOverlap
        ? OWL_OVERLAP_PRICE_TEXT_WIDTH
        : OWL_PRICE_TEXT_WIDTH;
    cells.price = buildCell('price', rowRect, priceLeft, priceLeft + priceWidth);
    const quantityLeft = OWL_BASE_BOUNDARIES[4];
    cells.quantity = buildCell('quantity', rowRect, quantityLeft, quantityLeft + OWL_QUANTITY_TEXT_WIDTH);
    return {
        index,
        rect: rowRect,
        cells,
        hasBillionPriceOverlap,
        ...(hasBillionPriceOverlap ? { billionDigitRect } : {}),
    };
}
export function buildOwlLayoutFromWindow(source, detection, options = {}) {
    if (!isOwlWindowRect(detection.rect)) {
        throw new Error(`Owl layout requires a ${OWL_WINDOW_WIDTH}×${OWL_WINDOW_HEIGHT} window; received ${detection.rect.width}×${detection.rect.height}`);
    }
    assertRectInside(source, detection.rect);
    const detectOverlap = options.detectBillionPriceOverlap ?? true;
    return {
        window: detection,
        itemName: relativeTo(detection.rect, ITEM_NAME_LOCAL),
        totalCount: relativeTo(detection.rect, TOTAL_COUNT_LOCAL),
        pageIndicator: relativeTo(detection.rect, PAGE_INDICATOR_LOCAL),
        rows: Array.from({ length: OWL_ROW_COUNT }, (_, index) => buildRowLayout(source, detection.rect, index, detectOverlap)),
    };
}
export function parseOwlLayout(source, options = {}) {
    if (options.windowRect && !isOwlWindowRect(options.windowRect))
        return null;
    const detection = options.windowRect
        ? knownDetection(options.windowRect)
        : detectOwlWindow(source, options);
    return detection ? buildOwlLayoutFromWindow(source, detection, options) : null;
}
//# sourceMappingURL=layout.js.map